import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router";
import { useSearchFilmsQuery, useSearchFilmsFullQuery, useSearchUsersQuery } from "../../services/api";
import FilmCard from "../../components/FilmCard/FilmCard";

const SearchResults: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get("q") || "";

  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Search bar state (start empty even if URL has q)
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  // Autocomplete Queries
  const { data: autoFilmsData, isFetching: autoFilmsFetching } = useSearchFilmsQuery({ query: searchQuery, limit: 5 }, { skip: searchQuery.length < 2 });
  const { data: autoUsersData, isFetching: autoUsersFetching } = useSearchUsersQuery({ query: searchQuery, limit: 5 }, { skip: searchQuery.length < 2 });
  const isSearching = autoFilmsFetching || autoUsersFetching;

  // Update autocomplete results
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      setShowAutocomplete(false);
      return;
    }

    const combinedResults = [
      ...(autoUsersData || []).map((u) => ({ ...u, type: "user" })),
      ...(autoFilmsData || []).map((f) => ({ ...f, type: f.type || "movie" })),
    ];

    if (combinedResults.length > 0) {
      setSearchResults(combinedResults);
      setShowAutocomplete(true);
    }
  }, [autoFilmsData, autoUsersData, searchQuery]);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [searchResults]);

  // Full search with API (on Enter)
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim().length < 2) return;
    setShowAutocomplete(false);
    try {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    } catch (err) {
      // console.error("Search error:", err);
    }
    // Reset local search box/autocomplete state so dropdown doesn't immediately reopen
    setSearchQuery("");
    setSearchResults([]);
    setShowAutocomplete(false);
    setSelectedIndex(-1);
  };

  // Navigate to chat or profile
  const handleAutocompleteClick = (item: any) => {
    if (item.type === "user") {
      navigate(`/profile/${item.username}`);
    } else {
      navigate(`/chat/${item.imdb_id}`);
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setShowAutocomplete(false);
  };

  // Main Search Queries
  const { data: mainFilmsData, isLoading: filmsLoading, isFetching: filmsFetching, error: filmsError } = useSearchFilmsFullQuery(query, { skip: !query || query.length < 2 });
  const { data: mainUsersData, isLoading: usersLoading, isFetching: usersFetching, error: usersError } = useSearchUsersQuery({ query: query, limit: 10 }, { skip: !query || query.length < 2 });

  // Reset state when query changes
  useEffect(() => {
    setLoading(true);
    setResults([]);
  }, [query]);

  useEffect(() => {
    if (!query || query.trim().length < 2) {
      navigate("/");
      return;
    }

    const isSearching = filmsLoading || usersLoading || filmsFetching || usersFetching;
    setLoading(isSearching);

    if (!isSearching) {
      const userResults = (mainUsersData || []).map((u) => ({ ...u, type: "user" }));
      const filmResults = (mainFilmsData || []).map((f) => ({ ...f, type: f.type || "movie" }));
      setResults([...userResults, ...filmResults]);
    }
    
    if (filmsError || usersError) {
       setError("An error occurred while searching");
    } else {
       setError(null);
    }

  }, [query, navigate, mainFilmsData, mainUsersData, filmsLoading, usersLoading, filmsFetching, usersFetching, filmsError, usersError]);

  // Close autocomplete when URL query changes (e.g., navigating away and back)
  useEffect(() => {
    setShowAutocomplete(false);
    setSearchResults([]);
    setSelectedIndex(-1);
  }, [query]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-950">
        <div className="text-white text-xl">Searching...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-gray-950 p-4">
        <div className="text-red-500 text-xl mb-4">{error}</div>
        <button onClick={() => navigate("/")} className="px-6 py-3 bg-red-600 rounded-lg hover:bg-red-700">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-gray-950 py-4">
      <div className="px-6 sm:px-10 lg:px-24">
        {/* Header + Search Bar */}
        <div className="mb-8">
          <button onClick={() => navigate("/")} className="text-red-600 hover:text-red-500 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>
          <form onSubmit={handleSearch} className="relative max-w-2xl mx-auto mb-6">
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchResults.length > 0 && setShowAutocomplete(true)}
              onBlur={() => setTimeout(() => setShowAutocomplete(false), 200)}
              placeholder="Search movies and TV shows..."
              className="w-full px-4 py-3 pl-12 pr-12 rounded-lg bg-gray-900 border-2 border-gray-700 focus:border-red-600 focus:outline-none text-white placeholder-gray-500"
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setSelectedIndex((prev) => Math.min(prev + 1, searchResults.length - 1));
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setSelectedIndex((prev) => Math.max(prev - 1, -1));
                } else if (e.key === "Enter") {
                  if (selectedIndex >= 0 && searchResults[selectedIndex]) {
                    e.preventDefault();
                    handleAutocompleteClick(searchResults[selectedIndex]);
                  }
                }
              }}
            />
            <svg
              className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            {isSearching && (
              <div className="absolute right-12 top-1/2 -translate-y-1/2">
                <div className="w-5 h-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            {/* Autocomplete Dropdown */}
            {showAutocomplete && searchResults.length > 0 && (
              <div className="absolute top-full mt-2 w-full bg-gray-900 border-2 border-red-600 rounded-lg shadow-lg max-h-96 overflow-y-auto z-50">
                {searchResults.map((item, index) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleAutocompleteClick(item)}
                    className={`w-full flex items-center gap-3 p-3 hover:bg-gray-800 transition-colors text-left border-b border-gray-800 last:border-b-0 ${
                      index === selectedIndex ? "bg-gray-700" : ""
                    }`}
                  >
                    <img
                      src={
                        item.type === "user"
                          ? item.avatar_url || "https://placehold.co/100x100/1f2937/ffffff/png?text=Avatar"
                          : (item.thumbnails ? JSON.parse(item.thumbnails)[0]?.url : item.primary_image) ||
                            "https://placehold.co/200x300/1f2937/ffffff/png?text=No+Image"
                      }
                      alt={item.type === "user" ? item.username : item.original_title}
                      className={`object-cover ${item.type === "user" ? "w-10 h-10 rounded-full border-2 border-cyan-600" : "w-12 h-16 rounded"}`}
                      loading="lazy"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.onerror = null;
                        target.src = item.type === "user" 
                          ? "https://placehold.co/100x100/1f2937/ffffff/png?text=Avatar"
                          : "https://placehold.co/200x300/1f2937/ffffff/png?text=No+Image";
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-semibold line-clamp-1 my-0">
                        {item.type === "user" ? item.username : item.original_title}
                        {item.type === "user" && item.display_name && (
                          <span className="text-xs text-gray-400 ml-2 font-normal">({item.display_name})</span>
                        )}
                      </h4>
                      <p className="text-sm text-gray-400">
                        {item.type === "user" ? (
                          <span className="text-cyan-400">User</span>
                        ) : (
                          <>
                            {item.type === "tvSeries" ? "TV Series" : "Movie"} • {item.start_year}
                            {item.average_rating && <span> • ⭐ {Number(item.average_rating).toFixed(1)}</span>}
                          </>
                        )}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </form>
          <h1 className="text-3xl font-bold text-white mb-2">Search Results for "{query}"</h1>
          <p className="text-gray-400">{results.length} results found</p>
        </div>

        {/* Results */}
        {results.length === 0 ? (
          <div className="text-center text-gray-500 py-16">
            <p className="text-xl mb-4">No results found</p>
            <p className="text-sm">Try searching with different keywords</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {results.map((item) => (
              item.type === "user" ? (
                <div key={item.id} className="bg-gray-900 rounded-lg p-4 flex flex-col items-center gap-3 border border-cyan-900 hover:border-cyan-500 transition-colors cursor-pointer" onClick={() => navigate(`/profile/${item.username}`)}>
                  <img
                    src={item.avatar_url || "https://placehold.co/100x100/1f2937/ffffff/png?text=Avatar"}
                    alt={item.username}
                    className="w-20 h-20 rounded-full border-2 border-cyan-500 object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.onerror = null;
                      target.src = "https://placehold.co/100x100/1f2937/ffffff/png?text=Avatar";
                    }}
                  />
                  <div className="text-center">
                    <h3 className="text-white font-bold text-lg truncate max-w-[150px]">{item.username}</h3>
                    {item.display_name && <p className="text-gray-400 text-sm truncate max-w-[150px]">{item.display_name}</p>}
                    <span className="inline-block mt-2 px-2 py-1 bg-cyan-900/50 text-cyan-400 text-xs rounded">User</span>
                  </div>
                </div>
              ) : (
                <FilmCard key={item.id} film={item} />
              )
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResults;
